import { inject, Injectable } from '@angular/core';
import { createORPCClient, createSafeClient } from '@orpc/client';
import { RPCLink } from '@orpc/client/fetch';
import { ContractRouterClient } from '@orpc/contract';
import { Auth } from '@angular/fire/auth';

import {
  EnhancedLoadingService,
  LoadingScope,
} from './enhanced-loading.service';
import {
  createAuthRetryPlugin,
  createErrorInterceptor,
  getAuthorizationHeader,
  OrpcClientContext,
} from './orpc-client.service';
import type { contract } from '#backend/contract';
import { environment } from '../../../environments/environment';
import { SnackbarService } from './snackbar.service';

// Mapear operações ORPC para escopos
const OPERATION_CONFIG: Record<string, LoadingScope> = {
  // operations - loading local no grid
  'socEnvAnalysis.byId': 'grid',
  'historiesSafras.byId': 'grid',
  'organization.contractSummary': 'grid',
  'admin.internalContractSummary': 'grid',

  'storage.getShpMetadata': 'input',
  'monitoring.availableCropYears': 'grid',
  'ibgeMunicipios.statesAbbreviations': 'grid',
  'ibgeMunicipios.states': 'grid',
  'ibgeMunicipios.citiesWithAbbreviations': 'grid',
  'ibgeMunicipios.cities': 'grid',
  'monitoring.results': 'grid',
  'monitoring.plotNDVI': 'global',
  'zarc.getSettings': 'grid',

  'users.list': 'grid',
  'users.checkLimit': 'input',
  'storage.upload': 'button',
  'storage.uploadDbf': 'button',
  'storage.uploadFgb': 'button',
} as const;

// Função para detectar escopo baseado na operação
function getLoadingScope(url: string, init?: RequestInit): LoadingScope {
  // Extrair operação da URL ORPC
  const urlPath = new URL(url).pathname;

  for (const [operation, scope] of Object.entries(OPERATION_CONFIG)) {
    if (urlPath.includes(operation.replace('.', '/'))) {
      return scope;
    }
    if (urlPath.includes('list')) return 'grid';
  }

  return 'global';
}

// Função para criar fetch inteligente com loading seletivo
function createSmartFetchWithLoading(loadingService: EnhancedLoadingService) {
  return async (input: RequestInfo, init?: RequestInit): Promise<Response> => {
    const url = typeof input === 'object' ? input.url : input.toString();
    const scope = getLoadingScope(url, init);

    // Detectar se é tarefa longa (upload de arquivos)
    const isLongTask =
      url.includes('upload') || (init?.body && init.body instanceof FormData);

    let hideLoading: () => void;
    console.log(
      `SmartFetch: ${scope} - ${isLongTask ? 'Long Task' : 'Short Task'} - ${url}`,
    );
    if (isLongTask) {
      hideLoading = loadingService.showWithTimeout(scope, 5000);
    } else {
      loadingService.show(scope);
      hideLoading = () => loadingService.hide(scope);
    }

    try {
      // 'include' para o cookie de sessão do manual de instruções sobreviver a um request
      // cross-origin localmente (frontend na 4200, backend na 3000) — sem isso o navegador
      // ignora o Set-Cookie de uma resposta cross-origin. Nos ambientes de nuvem BACKEND_HOST
      // é same-origin, então não muda nada lá.
      const response = await fetch(input, { ...init, credentials: 'include' });
      return response;
    } finally {
      hideLoading();
    }
  };
}

@Injectable({
  providedIn: 'root',
})
export class SmartOrpcClientService {
  readonly client: ContractRouterClient<typeof contract, OrpcClientContext>;
  readonly safeClient;

  private auth = inject(Auth);
  private loadingService = inject(EnhancedLoadingService);
  private snackBar = inject(SnackbarService);

  constructor() {
    this.client = createORPCClient(
      new RPCLink<OrpcClientContext>({
        url: environment.BACKEND_HOST + '/orpc',
        headers: async () => {
          const authorization = await getAuthorizationHeader(this.auth);
          return authorization ? { Authorization: authorization } : {};
        },
        fetch: createSmartFetchWithLoading(this.loadingService),
        plugins: [createAuthRetryPlugin(this.auth)],
        interceptors: [createErrorInterceptor(this.snackBar)],
      }),
    );
    this.safeClient = createSafeClient(this.client);
  }
}
